import React, {useState} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import {BASE_TEXT, WEIGHT, FONTS} from '../../../shared/style/typography';
import {LAPTOP, PHONE} from '../../../shared/style/breakpoints';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import {redirectTo} from '../../../shared/utils/navigation';
import {useSendAnalyticsEvent} from '../../../shared/enhancers/analytics-enhancer';
import {PLANS, CTAS} from '../../private-payment/constants';
import {CHARCOAL, FOCUS_BLUE, WHITE} from '../../../shared/style/colors.js';
import {ALPHA} from '../../../shared/style/color-utils';
import StackShareEnterpriseForm from '../../../shared/library/stackshare-enterprise-form';

const plans = [
  // {
  //   name: 'Forever Free',
  //   description: '',
  //   price: '$0',
  //   seats: '',
  //   features: [
  //     'Price - $0 Forever',
  //     'Sync up to 10 private repos',
  //     '1 VCS Provider',
  //     '1 VCS Org/Project',
  //     'Stack Alerts (Stack Changes)',
  //     'Automated Stack Decision creation',
  //     'Reporting dashboard',
  //     'Private Search',
  //     'Vulnerabilities Report [Security]'
  //   ],
  //   seatOptions: [],
  //   isActive: true,
  //   isFeatured: false,
  //   isPerMonth: false,
  //   slug: PLANS.FREE
  // },
  // {
  //   name: 'Team',
  //   description: 'Team plan description',
  //   price: '$500',
  //   seats: 'per month',
  //   features: [
  //     'Price - $500 Per Month',
  //     'Sync up to 1,000 private repos',
  //     '1 VCS Provider',
  //     '1 VCS Org/Project',
  //     'Stack Alerts (Stack Changes)',
  //     'Automated Stack Decision creation',
  //     'Reporting dashboard',
  //     'Private Search',
  //     'Vulnerabilities Report [Security]',
  //     '9-5 support with 24-hour first response time on business days',
  //     'Stack Applications',
  //     'Stack Tags',
  //     'Stack Watching',
  //     'Active Directory/Okta Single Sign-On (SSO)'
  //   ],
  //   seatOptions: [],
  //   isActive: false,
  //   isFeatured: false,
  //   isPerMonth: false,
  //   slug: PLANS.TEAM
  // },
  {
    name: 'Enterprise',
    description: 'Enterprise plan description',
    seats: '',
    features: [
      'Price - Contact Us',
      'Sync unlimited private repos',
      'Unlimited VCS Providers',
      'Unlimited VCS Org/Project',
      'Stack Alerts (Stack Changes)',
      'Automated Stack Decision creation',
      'Reporting dashboard',
      'Private Search',
      'Vulnerabilities Report [Security]',
      '24-hour support with four-hour first response time on business days',
      'Stack Applications',
      'Stack Tags',
      'Stack Watching',
      'Active Directory/Okta Single Sign-On (SSO)',
      'Tool Adoption Stages [Governance]',
      'License Data [Compliance]',
      '99% guaranteed uptime SLA',
      'API Access'
    ],
    seatOptions: [],
    isActive: false,
    isFeatured: true,
    isPerMonth: false,
    slug: PLANS.ENTERPRISE
  }
];

const Container = glamorous.div({
  ...BASE_TEXT,
  width: '100%',
  maxWidth: MAX_PAGE_WIDTH,
  padding: '50px 20px 75px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '> img': {
    maxWidth: 800,
    height: 'auto'
  },
  [PHONE]: {
    width: '90%',
    padding: '63px 0 118px',
    margin: '0 auto',
    '> img': {
      width: '100%'
    }
  }
});

const Heading = glamorous.h3({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontWeight: WEIGHT.ULTRA_BLACK,
  margin: '25px 0',
  [PHONE]: {
    margin: 0,
    fontSize: 28,
    lineHeight: '40px'
  },
  fontSize: '38px',
  textAlign: 'center'
});

export const NoticeContainer = glamorous.div({
  margin: '20px auto',
  '& > aside > div > div': {
    paddingTop: 4,
    paddingBottom: 4,
    '> div': {
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 2
    }
  },
  [PHONE]: {
    '& > aside > div > div': {
      padding: '4px 10px',
      '> div': {
        fontSize: 9
      }
    },
    fontSize: 9,
    padding: 0
  }
});

const PlansWrapper = glamorous.div({
  marginTop: 72,
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '0 30px',
  width: '360px',
  [LAPTOP]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 0
  }
});

const PlanContainer = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [PHONE]: {
    marginTop: 33
  }
});

const PlanHeading = glamorous.div({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  lineHeight: '53px',
  color: CHARCOAL,
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 34,
  textAlign: 'center',
  marginBottom: 33,
  [PHONE]: {
    fontSize: 28,
    margin: 0
  }
});

const ButtonContainer = glamorous(SimpleButton)(
  {
    border: `1px solid #008FF9`,
    borderRadius: 4,
    height: 58,
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: FOCUS_BLUE,
    color: WHITE,
    padding: '13px 41px',
    width: '100%',
    fontFamily: FONTS.CEREBRI_SANS_PRO,
    [PHONE]: {
      fontSize: 20,
      fontWeight: 700,
      margin: '23px 0 0'
    }
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const List = glamorous.ul(
  {
    listStyle: 'none',
    marginTop: 33,
    marginBottom: 0,
    border: `1px solid ${CHARCOAL}`,
    borderRadius: 16,
    padding: '45px 29px 33px 33px',
    [PHONE]: {
      margin: '23px 0 0'
    }
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const ListItem = glamorous.li(
  {
    display: 'flex'
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const CheckboxStyled = glamorous.img(
  {
    width: 12,
    height: 12,
    marginRight: 16
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const FeatureText = glamorous.p(
  {
    marginTop: -10,
    fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
    fontSize: 16,
    fontWeight: WEIGHT.NORMAL,
    lineHeight: '34px',
    color: CHARCOAL,
    [PHONE]: {
      fontSize: 16,
      lineHeight: '40px'
    }
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const Pricing = ({currentUser}) => {
  const [isLeadFormOpen, setLeadFormOpen] = useState(false);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const choosePlan = item => {
    if (item.slug === PLANS.ENTERPRISE) {
      setLeadFormOpen(true);
      sendAnalyticsEvent('request_demo', {
        index: 5,
        sectionName: 'PrivateTeamPricing'
      });
    } else {
      redirectTo(`/manage-companies/join`);
    }
  };

  return (
    <Container id="pricing">
      <Heading>StackShare Enterprise Pricing</Heading>

      <PlansWrapper>
        {plans.slice(0, 3).map((plan, index) => {
          const hideButtons =
            currentUser &&
            currentUser.privateMode &&
            (plan.slug === PLANS.FREE || plan.slug === PLANS.TEAM);
          return (
            <PlanContainer key={index}>
              <PlanHeading>{plan.name}</PlanHeading>
              {!hideButtons && plan.slug !== PLANS.ENTERPRISE && (
                <ButtonContainer
                  onClick={() => {
                    choosePlan(plan);
                    sendAnalyticsEvent('pricing', {
                      plan: plan
                    });
                  }}
                >
                  {plan.slug === PLANS.TEAM ? 'Start FREE 7-day trial' : 'Sign Up'}
                </ButtonContainer>
              )}
              {plan.slug === PLANS.ENTERPRISE && (
                <ButtonContainer
                  onClick={() => {
                    choosePlan(plan);
                    sendAnalyticsEvent('pricing', {
                      plan: plan
                    });
                  }}
                  extraStyle={{
                    backgroundColor: CHARCOAL,
                    borderColor: CHARCOAL,
                    ':hover': {
                      backgroundColor: ALPHA(CHARCOAL, 0.8),
                      borderColor: ALPHA(CHARCOAL, 0.79)
                    }
                  }}
                >
                  {CTAS.CONTACT_US}
                </ButtonContainer>
              )}
              <List
                extraStyle={
                  plan.slug === PLANS.ENTERPRISE
                    ? {
                        backgroundColor: CHARCOAL,
                        color: WHITE
                      }
                    : {
                        marginTop: hideButtons ? 93 : 33
                      }
                }
              >
                {plan.features.map((feature, index) => (
                  <ListItem
                    key={index}
                    extraStyle={
                      plan.slug === PLANS.ENTERPRISE
                        ? {
                            color: WHITE
                          }
                        : {}
                    }
                  >
                    <CheckboxStyled
                      src={
                        plan.slug === PLANS.ENTERPRISE
                          ? 'https://img.stackshare.io/fe/private/checkbox_white.png'
                          : 'https://img.stackshare.io/fe/private/checkbox.png'
                      }
                      alt="checkbox"
                    />
                    <FeatureText
                      extraStyle={{
                        color: plan.slug === PLANS.ENTERPRISE ? WHITE : CHARCOAL
                      }}
                    >
                      {feature}
                    </FeatureText>
                  </ListItem>
                ))}
              </List>
            </PlanContainer>
          );
        })}
      </PlansWrapper>

      {isLeadFormOpen && (
        <StackShareEnterpriseForm
          onDismiss={() => setLeadFormOpen(false)}
          title="Have questions before you sign up?"
          type="lead"
        />
      )}
    </Container>
  );
};

Pricing.propTypes = {
  currentUser: PropTypes.object
};

export default Pricing;
